import { useState } from 'react'
import { describeAuthError, type AuthErrorSignals } from '../../lib/authErrors'

interface AuthErrorScreenProps {
  /**
   * Ce que MSAL rapporte : code OAuth, message, et le fait qu'une interaction soit requise.
   * La décision se prend sur le code et le type — **pas** sur le texte, que Microsoft se réserve
   * de changer (cf. `authErrors.ts`).
   */
  signals: AuthErrorSignals
  /** Relance une tentative — c'est le callback `login` de `useMsalAuthentication`. */
  onRetry: () => void
}

/**
 * Issue #43 — l'écran qui remplace la boucle silencieuse.
 *
 * Il n'a PAS de route : au retour d'Entra en erreur l'utilisateur est non authentifié sur
 * `/auth/callback`, qui n'existe pas dans l'arbre de routes non authentifié — il tombe donc dans
 * le `*`, c'est-à-dire `LoginPage`. C'est elle qui rend cet écran au lieu de rediriger.
 */
export function AuthErrorScreen({ signals, onRetry }: AuthErrorScreenProps) {
  const clientId = import.meta.env['VITE_MSAL_CLIENT_ID'] as string
  // L'origine courante EST une URI de redirection enregistrée dans l'app registration (c'est par
  // elle que l'utilisateur vient d'arriver) : le lien ramène donc l'administrateur sur ce portail,
  // et s'adapte de lui-même à dev / tests / prod sans table de correspondance.
  const redirectUri = `${window.location.origin}/auth/callback`
  const view = describeAuthError(signals, clientId, redirectUri)
  // `describeAuthError` retombe déjà sur `errorCode` quand Entra ne renvoie aucun numéro AADSTS
  // (`user_cancelled`, `interaction_in_progress`, `hash_empty_error`…) : sans quoi l'écran
  // inviterait à « transmettre le code » sans en afficher aucun.
  const shownCode = view.code
  const shownDetail = view.detail || signals.errorCode || ''
  const [copied, setCopied] = useState(false)

  const copyLink = () => {
    if (!view.adminConsentUrl) return
    void navigator.clipboard.writeText(view.adminConsentUrl).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      },
      () => setCopied(false)
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background px-4">
      <div className="max-w-lg w-full">
        <h1 className="font-heading text-2xl text-primary mb-4">{view.title}</h1>
        <p className="font-body text-muted-foreground mb-6">{view.explanation}</p>

        {view.adminConsentUrl && (
          <div className="mb-6 rounded-md border border-border bg-highlight p-4">
            <p className="font-body text-xs text-muted-foreground mb-2">
              Lien à transmettre à votre administrateur
            </p>
            <code className="block font-mono text-xs text-primary break-all mb-3">
              {view.adminConsentUrl}
            </code>
            <button
              type="button"
              onClick={copyLink}
              className="px-4 py-1.5 bg-primary text-white font-body text-sm rounded-md hover:bg-accent transition-colors"
            >
              {copied ? 'Lien copié' : 'Copier le lien'}
            </button>
          </div>
        )}

        {/* Le code AADSTS reste lisible dans tous les cas : c'est lui qui distingue un tenant non
            consenti d'une URL de redirection non enregistrée, et c'est lui qu'un client nous
            transmettra. */}
        {shownCode && (
          <p className="font-body text-sm text-muted-foreground mb-1">
            Code Microsoft : <code className="font-mono text-primary">{shownCode}</code>
          </p>
        )}
        {shownDetail && (
          <details className="mb-6">
            <summary className="font-body text-sm text-accent cursor-pointer">
              Détail technique
            </summary>
            <p className="font-mono text-xs text-muted-foreground mt-2 break-words">{shownDetail}</p>
          </details>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onRetry}
            className="px-6 py-2 bg-primary text-white font-body rounded-md hover:bg-accent transition-colors"
          >
            Réessayer
          </button>
          <a
            href="mailto:support@pragmelis.com"
            className="px-6 py-2 border border-border text-primary font-body rounded-md hover:bg-highlight transition-colors"
          >
            Contacter le support
          </a>
        </div>
      </div>
    </div>
  )
}
